<template>
  <div class="ChatStart">
    <v-container class="ChatStart__container">
      <v-row class="ChatStart__welcome" align="center" justify="center">
        <v-col cols="auto" class="d-flex align-center">
          <img
            :src="`${publicPath}/icons/ai_response.svg`"
            alt="AI Assistant"
            height="45"
          />
          <h1 class="text-h4 font-weight-medium">
            Hello{{
              this.AICard?.User?.firstName
                ? ', ' + this.AICard.User.firstName
                : ''
            }}!
          </h1>
        </v-col>
      </v-row>

      <v-row class="ChatStart__controls" align="center" justify="space-between">
        <v-col cols="auto">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="primary"
                outlined
                v-bind="attrs"
                v-on="on"
                class="text-capitalize"
                :class="{ 'primary--text': !chatWorkspace }"
              >
                <v-icon left>mdi-account-group-outline</v-icon>
                {{ chatWorkspace?.name || 'Select Agent' }}
                <v-icon right>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="selectWorkspace(null)">
                <v-list-item-title :class="{ 'primary--text': !chatWorkspace }">
                  None
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                v-for="(item, i) in workspaces"
                :key="i"
                @click="selectWorkspace(item)"
              >
                <v-list-item-title
                  :class="{ 'primary--text': chatWorkspace?.id === item.id }"
                >
                  {{ item.name }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>

      <v-row class="ChatStart__input">
        <v-col>
          <v-card
            outlined
            class="rounded-xxl"
            style="padding: 5px; width: 100%"
            @dragover.prevent
            @drop.prevent="onFileDrop"
          >
            <v-textarea
              v-model="message"
              class="ChatStart__textarea"
              hide-details
              rows="4"
              no-resize
              placeholder="Type your message here..."
              @keydown.enter.exact.prevent="send"
              @keydown.enter.ctrl.exact.prevent="() => {}"
              @keydown.enter.shift.exact.prevent="() => {}"
              id="message_input"
            ></v-textarea>

            <v-card-actions class="pa-1">
              <v-btn icon>
                <v-file-input
                  v-model="files"
                  hide-input
                  prepend-icon="mdi-paperclip"
                  multiple
                  accept=".doc,.docx,.pdf,.csv,.txt,.html,.odt,.rtf,.epub,.jpeg,.png,.jpg,image/*"
                  @change="onFileChange"
                ></v-file-input>
              </v-btn>

              <v-btn
                icon
                color="grey darken-1"
                @click.stop="isSettingsOpen = true"
              >
                <v-icon> mdi-cog </v-icon>
              </v-btn>

              <v-spacer></v-spacer>

              <v-btn
                v-if="
                  (message?.trim()?.replaceAll('\n', '') || '') !== '' &&
                  chatWorkspace?.id
                "
                @click.stop="saveWorkspacePrompt"
                :loading="workspaceActionLoading"
                text
                color="primary"
                class="mr-2"
              >
                Save Prompt
              </v-btn>

              <v-btn
                color="primary"
                fab
                small
                @click.stop="send"
                :loading="sendMessageLoading"
                :disabled="sendMessageLoading || !message.trim()"
              >
                <v-icon>mdi-send</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>

          <workspace-prompt
            class="mt-2"
            @onPromptSelect="
              (data) => {
                this.message = data.prompt;
              }
            "
          />

          <v-row
            v-if="filesAccumulated.length !== 0 || linkPreviews.length !== 0"
            class="Chat__files"
          >
            <v-card outlined class="rounded-lg" width="100%">
              <v-card-text class="pa-0">
                <div class="filesContainer">
                  <div
                    v-for="(file, index) in filesAccumulated"
                    :key="index"
                    class="fileItem"
                  >
                    <v-card outlined>
                      <v-card-text class="pa-2 d-flex align-center">
                        <v-btn
                          icon
                          x-small
                          class="mr-2"
                          @click.stop="onFileDelete(index)"
                        >
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>

                        <v-avatar
                          v-if="file.type.includes('image')"
                          size="32"
                          class="mr-2"
                        >
                          <v-img :src="createObjectURL(file)" />
                        </v-avatar>

                        <v-avatar v-else size="32" color="primary" class="mr-2">
                          <v-icon small color="white">mdi-file</v-icon>
                        </v-avatar>

                        <span class="text-body-2 text-truncate">{{
                          file.name
                        }}</span>

                        <v-btn
                          :title="
                            filesMetadata[index].isPatientList
                              ? 'Unmark'
                              : 'Mark as patient list'
                          "
                          icon
                          small
                          class="mr-2"
                          @click.stop="updatePatientListStatus(index, 'file')"
                        >
                          <v-icon>{{
                            !filesMetadata[index].isPatientList
                              ? 'mdi-account-multiple-remove'
                              : 'mdi-account-multiple-check'
                          }}</v-icon>
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </div>

                  <div
                    v-for="(linkData, index) in linkPreviews"
                    :key="index"
                    class="fileItem"
                    @click.stop="openLink(linkData.url)"
                  >
                    <v-card outlined>
                      <v-card-text class="pa-2 d-flex align-center">
                        <v-btn
                          icon
                          x-small
                          class="mr-2"
                          @click.stop="onLinkDelete(index)"
                        >
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>

                        <v-avatar
                          size="32"
                          class="mr-2"
                          v-if="linkData?.favicons?.length > 0"
                        >
                          <v-img
                            :src="
                              linkData?.favicons?.length > 0
                                ? linkData?.favicons[0]
                                : ''
                            "
                          />
                        </v-avatar>

                        <div>
                          <div>
                            <span
                              class="text-body-2 text-truncate"
                              style="max-width: 250px; display: block"
                              >{{ linkData?.title || linkData.url }}</span
                            >
                          </div>
                          <div>
                            <span
                              class="text-body-2 text-truncate"
                              style="width: 250px; display: block"
                              >{{ linkData?.description }}</span
                            >
                          </div>
                        </div>
                        <v-btn
                          :title="
                            linkAttachments[index].isPatientList
                              ? 'Unmark'
                              : 'Mark as patient list'
                          "
                          icon
                          small
                          class="mr-2"
                          @click.stop="updatePatientListStatus(index, 'link')"
                        >
                          <v-icon>{{
                            !linkAttachments[index].isPatientList
                              ? 'mdi-account-multiple-remove'
                              : 'mdi-account-multiple-check'
                          }}</v-icon>
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-row>
        </v-col>
      </v-row>

      <v-row class="ChatStart__sessions">
        <div class="d-flex align-center mb-4 justify-end p-0">
          <v-btn text color="primary" @click.stop="goToSessions()">
            View Recent Chats
            <v-icon right>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </v-row>
    </v-container>

    <a-i-chat-settings
      :dialog="isSettingsOpen"
      @close="isSettingsOpen = false"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

const { mapGetters: customerGetters, mapActions: customerActions } =
  createNamespacedHelpers('customer');
const { mapActions: sharedActions } = createNamespacedHelpers('shared');
const {
  mapGetters: AIChatGetters,
  mapActions: AIChatActions,
  mapMutations: AIChatMutations,
} = createNamespacedHelpers('AIChat');
const {
  mapGetters: workspacesGetters,
  mapActions: workspaceActions,
  mapMutations: workspacesMutations,
} = createNamespacedHelpers('aiWorkspace');

import constants from '@/util/constants';
import AIChatSettings from '@/views/ai-chat/ai-chat-settings/ai-chat-settings.vue';
import WorkspacePrompt from '@/views/ai-workspaces/workspace/workspace-prompts/workspace-prompts.vue';
import * as moment from 'moment/moment';

export default {
  name: 'ChatStartPage',
  components: { WorkspacePrompt, AIChatSettings },
  data() {
    return {
      isMemoryEditOpen: false,
      memory: {
        now: true,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      sessionPage: 0,
      sessionLimit: 6,
      message: '',

      files: [],
      filesAccumulated: [],
      filesMetadata: [],

      publicPath: constants.isDevelopment ? '' : '',
      selectedWorkspace: null,
      isSettingsOpen: false,

      linkPreviews: [],
      linkAttachments: [],
    };
  },
  computed: {
    ...customerGetters({
      customer: 'CUSTOMER',
      user: 'USER',
      selectedCustomer: 'SELECTED_CUSTOMER',
    }),
    ...AIChatGetters({
      AICard: 'GET_USER_AI_CARD',
      sendMessageLoading: 'SEND_MESSAGE_LOADING',
      sessions: 'SESSIONS_LIST',
      sessionListLoading: 'SESSIONS_LIST_LOADING',
      chatWorkspace: 'GET_CHAT_WORKSPACE',
    }),
    ...workspacesGetters({
      workspaces: 'WORKSPACES_LIST',
      workspacesLoading: 'WORKSPACES_LIST_LOADING',
      workspaceActionLoading: 'IS_WORKSPACE_PROMPTS_ACTION_LOADING',
      selectedPrompt: 'SELECTED_WORKSPACE_PROMPT',
      workspacePrompts: 'WORKSPACE_PROMPTS',
    }),
  },
  methods: {
    ...sharedActions(['getLinkPreview']),
    ...customerActions(['getCustomerData']),
    ...AIChatActions([
      'sendMessage',
      'getToolSets',
      'getSystemPrompts',
      'updateSystemPrompt',
      'createSystemPrompt',
      'getSessions',
    ]),
    ...AIChatMutations({
      setSendMessageLoading: 'SET_SEND_MESSAGE_LOADING',
      setSendMessage: 'SET_SEND_MESSAGE',
      setChatWorkspace: 'SET_CHAT_WORKSPACE',
      setSessions: 'SET_SESSIONS_LIST',
    }),
    ...workspaceActions([
      'getWorkspaces',
      'getWorkspaceSessions',
      'updateWorkspace',
      'updateWorkspacePrompt',
      'createWorkspacePrompt',
    ]),
    ...workspacesMutations({
      pushToWorkspacePrompt: 'PUSH_TO_WORKSPACE_PROMPTS',
      setWorkspacePrompts: 'SET_WORKSPACE_PROMPTS',
    }),
    updatePatientListStatus(index, type) {
      if (type === 'link') {
        this.linkAttachments[index].isPatientList =
          !this.linkAttachments[index].isPatientList;
      }
      if (type === 'file') {
        this.filesMetadata[index].isPatientList =
          !this.filesMetadata[index].isPatientList;
      }
    },
    openLink(url) {
      window.open(url, '_blank');
    },
    async saveWorkspacePrompt() {
      if (this.selectedPrompt?.id) {
        const prompt = await this.updateWorkspacePrompt({
          prompt: this.message,
          workspaceId: this.chatWorkspace.id,
          workspacePromptId: this.selectedPrompt?.id,
        });
        const prompts = [...this.workspacePrompts];
        const index = prompts.findIndex((item) => item?.id === prompt?.id);
        prompts[index] = prompt;
        this.setWorkspacePrompts([...prompts]);
        return;
      }
      const prompt = await this.createWorkspacePrompt({
        prompt: this.message,
        workspaceId: this.chatWorkspace.id,
      });
      this.pushToWorkspacePrompt(prompt);
    },
    createObjectURL(file) {
      return URL.createObjectURL(file);
    },
    getTime(date) {
      return moment(date).fromNow();
    },
    onFileDelete(index) {
      this.filesAccumulated.splice(index, 1);
      this.filesMetadata.splice(index, 1);
      this.files = [];
    },
    selectWorkspace(item) {
      this.setChatWorkspace(item);
    },
    onFileChange(event) {
      event.forEach((thisFile) => {
        if (this.validateSize(thisFile)) {
          this.$store.dispatch(
            'notifications/setError',
            `${thisFile.name} too big`,
          );
          return;
        }
        this.filesAccumulated.push(thisFile);
        this.filesMetadata.push({ isPatientList: false });
      });
      this.files = [];
    },
    onLinkDelete(index) {
      this.linkAttachments.splice(index, 1);
      this.linkPreviews.splice(index, 1);
    },
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
    },
    async prepareFiles() {
      const prepared = await Promise.all(
        this.filesAccumulated.map(async (file) => {
          const base64 = await this.toBase64(file);
          return {
            base64: base64,
            name: file.name,
            type: file.type,
          };
        }),
      );
      return prepared;
    },
    validateSize(input) {
      const fileSize = input.size / 1024 / 1024; // in MiB
      return fileSize > 1;
    },
    async send() {
      if (this.message === '') {
        return;
      }
      const messageValue = this.message.valueOf();
      this.message = '';
      this.setSendMessageLoading(true);

      this.setSendMessage({
        workspaceId: this.chatWorkspace?.id,
        workspace: this.chatWorkspace,
        files: this.filesAccumulated,
        filesMetadata: this.filesMetadata,
        linkAttachments: this.linkAttachments,
        messageValue,
        memory: this.memory,
      });

      this.$router.push({ path: `/ai-chat` });
    },
    async getInfo() {
      await this.getWorkspaces();
      await this.getSessions({
        page: this.sessionPage,
        limit: this.sessionLimit,
      });
      await this.autoSelectDefaultWorkspace();
    },
    async autoSelectDefaultWorkspace() {
      if (this.workspaces?.length > 0 && !this.chatWorkspace?.id) {
        let selectedWorkspace = null;

        const lastUsedAgentId = localStorage.getItem('lastUsedAgentId');
        if (lastUsedAgentId) {
          selectedWorkspace = this.workspaces.find(
            (workspace) => workspace.id === parseInt(lastUsedAgentId),
          );
        }

        if (
          !selectedWorkspace &&
          this.customer?.CustomersFeature?.default_workspace_id
        ) {
          selectedWorkspace = this.workspaces.find(
            (workspace) =>
              workspace.id ===
              this.customer.CustomersFeature.default_workspace_id,
          );
        }

        if (selectedWorkspace) {
          this.selectWorkspace(selectedWorkspace);
        }
      }
    },
    goToSessions() {
      this.$router.push({ path: `/chat-sessions` });
    },
    onFileDrop(e) {
      if (!e.dataTransfer || !e.dataTransfer.files) return;
      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length === 0) return;
      this.onFileChange(droppedFiles);
    },
  },
  watch: {
    async 'selectedCustomer.customer_id'() {
      this.setChatWorkspace(null);
      this.setSessions([]);
      await this.getInfo();
    },
  },
  async mounted() {
    const { workspaceId } = this.$route.params;
    await this.getInfo(workspaceId);

    document
      .getElementById('message_input')
      .addEventListener('keydown', async (e) => {
        const getCaret = (el) => {
          if (el.selectionStart) {
            return el.selectionStart;
          } else if (document.selection) {
            el.focus();
            const r = document.selection.createRange();
            if (r == null) {
              return 0;
            }
            const re = el.createTextRange(),
              rc = re.duplicate();
            re.moveToBookmark(r.getBookmark());
            rc.setEndPoint('EndToStart', re);
            return rc.text.length;
          }
          return 0;
        };

        const start = e.target.selectionStart;
        const end = e.target.selectionEnd;
        const caret = getCaret(e.target);
        if (
          e.key == 'Enter' &&
          e.ctrlKey == false &&
          e.shiftKey == false &&
          this.message.replaceAll('\n', '') !== ''
        ) {
          await this.send();
        }
        if (e.key == 'Enter' && e.ctrlKey == true) {
          this.message =
            this.message.substring(0, caret) +
            '\n' +
            this.message.substring(end);
          e.stopPropagation();
          setTimeout(() => {
            e.target.selectionStart = e.target.selectionEnd = start + 1;
          }, 1);
        }
        if (e.key == 'Enter' && e.shiftKey == true) {
          this.message =
            this.message.substring(0, caret) +
            '\n' +
            this.message.substring(end);
          e.stopPropagation();
          setTimeout(() => {
            e.target.selectionStart = e.target.selectionEnd = start + 1;
          }, 1);
        }
      });
    document.getElementById('message_input').onpaste = async (event) => {
      const items = (
        event?.clipboardData || event?.originalEvent?.clipboardData
      ).items;
      for (const index in items) {
        const item = items[index];
        if (item.kind === 'file') {
          const blob = item.getAsFile();
          this.filesAccumulated ??= [];
          this.filesAccumulated.push(blob);
          continue;
        }

        let pastedText = '';
        if (window.clipboardData && window.clipboardData.getData) {
          // IE
          pastedText = window.clipboardData.getData('Text');
        } else if (event.clipboardData && event.clipboardData.getData) {
          pastedText = event.clipboardData.getData('text/plain');
        }
        // eslint-disable-next-line
        const linkRegex = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/;

        if (linkRegex.test(pastedText)) {
          const preview = await this.getLinkPreview(pastedText);
          this.linkPreviews.push(preview);
          this.linkAttachments.push({
            url: pastedText,
            isPatientList: false,
          });
        }
      }
    };
  },
};
</script>

<style lang="scss" scoped>
@import './chat-start-page';
</style>
